import { useCallback, useRef, useState } from 'react';
interface CameraError {
  title: string;
  message: string;
}
interface UseCameraReturn {
  hasPermission: boolean | null;
  isFrontCamera: boolean;
  stream: MediaStream | null;
  capturedImage: string | null;
  videoRef: React.RefObject<HTMLVideoElement>;
  cameraError: CameraError;
  startCamera: (facingMode?: 'user' | 'environment') => Promise<void>;
  stopCamera: () => void;
  capturePhoto: () => void;
  handleTryAgain: () => void;
  setupVideo: () => Promise<void>;
}
// Estado global para controlar a câmera
const globalCameraState = {
  currentStream: null as MediaStream | null,
  isStarting: false,
};
export const useCamera = (): UseCameraReturn => {
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [isFrontCamera, setIsFrontCamera] = useState(true);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [currentFacingMode, setCurrentFacingMode] = useState<
    'user' | 'environment'
  >('user');
  const videoRef = useRef<HTMLVideoElement>(null);
  const [cameraError, setCameraError] = useState<CameraError>({
    title: '',
    message: '',
  });
  const retryTimeoutRef = useRef<NodeJS.Timeout>();
  const handleVideoPlay = async (video: HTMLVideoElement) => {
    try {
      await video.play();
    } catch (error) {
      throw new Error('Erro ao iniciar vídeo');
    }
  };
  const stopCameraGlobal = useCallback(() => {
    if (globalCameraState.currentStream) {
      globalCameraState.currentStream.getTracks().forEach((track) => {
        track.stop();
      });
      globalCameraState.currentStream = null;
    }
    // Reset video element
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setStream(null);
  }, []);
  const stopCamera = useCallback(() => {
    stopCameraGlobal();
  }, [stopCameraGlobal]);
  const tryStartCamera = useCallback(
    async (
      facingMode: 'user' | 'environment',
      retryCount = 0,
    ): Promise<void> => {
      const maxRetries = 3;
      const baseDelay = 500;
      try {
        // Parar completamente qualquer stream anterior
        stopCameraGlobal();
        // Delay baseado no número de tentativas
        const delay = baseDelay * (retryCount + 1);
        await new Promise((resolve) => setTimeout(resolve, delay));

        let mediaStream: MediaStream;
        try {
          mediaStream = await navigator.mediaDevices.getUserMedia({
            video: {
              facingMode: { exact: facingMode },
            },
          });
        } catch (exactError) {
          await new Promise((resolve) => setTimeout(resolve, 200));
          mediaStream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode },
          });
        }
        globalCameraState.currentStream = mediaStream;
        setStream(mediaStream);
        setHasPermission(true);
        setCameraError({ title: '', message: '' });
      } catch (error) {
        if (
          retryCount < maxRetries &&
          (error as Error).name === 'NotReadableError'
        ) {
          // Tentar novamente após um delay
          retryTimeoutRef.current = setTimeout(
            () => {
              tryStartCamera(facingMode, retryCount + 1);
            },
            1000 * (retryCount + 1),
          );

          // Mostrar mensagem de retry para o usuário
          setCameraError({
            title: 'Iniciando câmera...',
            message: `Tentativa ${retryCount + 1} de ${maxRetries + 1}. Aguarde...`,
          });
          setHasPermission(null);
        } else {
          handleCameraError(error as Error);
        }
      }
    },
    [stopCameraGlobal],
  );

  const startCamera = useCallback(
    async (facingMode: 'user' | 'environment' = 'user') => {
      // Evitar múltiplas tentativas simultâneas
      if (globalCameraState.isStarting) {
        // Câmera já está sendo iniciada, ignorando
        return;
      }
      // Limpar retry anterior se existir
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      globalCameraState.isStarting = true;
      try {
        // Reset states
        setHasPermission(null);
        setCameraError({ title: '', message: '' });
        setCapturedImage(null);
        // Update states based on facingMode
        setIsFrontCamera(facingMode === 'user');
        setCurrentFacingMode(facingMode);

        await tryStartCamera(facingMode, 0);
      } finally {
        globalCameraState.isStarting = false;
      }
    },
    [tryStartCamera],
  );

  const handleCameraError = (error: Error) => {
    if (
      error.name === 'NotFoundError' ||
      error.name === 'DevicesNotFoundError'
    ) {
      setCameraError({
        title: 'Nenhuma câmera foi encontrada',
        message: 'Por favor, verifique se a câmera está ligada e habilitada.',
      });
    } else if (
      error.name === 'NotAllowedError' ||
      error.name === 'PermissionDeniedError'
    ) {
      setCameraError({
        title: 'Permissão negada',
        message:
          'Para continuar, precisamos do acesso à sua câmera. Por favor, permita o acesso nas configurações do seu navegador.',
      });
    } else if (error.name === 'OverconstrainedError') {
      setCameraError({
        title: 'Câmera não suportada',
        message:
          'O modo de câmera solicitado não é suportado neste dispositivo.',
      });
    } else if (error.name === 'NotReadableError') {
      setCameraError({
        title: 'Erro ao acessar câmera',
        message:
          'Não foi possível acessar a câmera. Tente recarregar a página.',
      });
    } else {
      setCameraError({
        title: 'Erro ao iniciar câmera',
        message: `Por favor, tente novamente. Erro: ${error.message}`,
      });
    }
    setHasPermission(false);
  };

  const capturePhoto = () => {
    if (videoRef.current) {
      const canvas = document.createElement('canvas');
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      const ctx = canvas.getContext('2d');

      if (ctx) {
        ctx.drawImage(videoRef.current, 0, 0);
        const imageData = canvas.toDataURL('image/jpeg');
        setCapturedImage(imageData);
      }
    }
  };
  const setupVideo = useCallback(async () => {
    if (stream && videoRef.current && videoRef.current.srcObject !== stream) {
      try {
        videoRef.current.srcObject = stream;
        await new Promise((resolve, reject) => {
          if (videoRef.current) {
            videoRef.current.onloadedmetadata = () => {
              resolve(true);
            };
            videoRef.current.onerror = (error) => {
              reject(error);
            };
            // Timeout para evitar que fique esperando forever
            setTimeout(() => {
              reject(new Error('Timeout loading video'));
            }, 10000);
          }
        });
        if (videoRef.current) {
          await handleVideoPlay(videoRef.current);
        }
      } catch (error) {
        handleCameraError(error as Error);
      }
    }
  }, [stream]);
  const handleTryAgain = useCallback(() => {
    setCapturedImage(null);
    startCamera(currentFacingMode);
  }, [currentFacingMode, startCamera]);
  return {
    hasPermission,
    isFrontCamera,
    stream,
    capturedImage,
    videoRef,
    cameraError,
    startCamera,
    stopCamera,
    capturePhoto,
    handleTryAgain,
    setupVideo,
  };
};
